/**
 * Recommendation Engine Page
 * محرك التوصيات - ربط احتياجات المستخدمين بالخدمات
 * 
 * CRITICAL: AI depends ONLY on Category + Service (NOT Program)
 */

import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
  ShoppingBag, TrendingUp, GraduationCap, MessageSquare, Building2,
  Wrench, Megaphone, Rocket, Lightbulb, Plus, Edit2, Trash2, ToggleLeft,
  ToggleRight, ChevronLeft, ChevronRight, X, Check, AlertTriangle,
  Info, Zap, Eye, ArrowLeft, Bot, User, Sparkles, HelpCircle,
  Search, Filter, RefreshCw, CheckCircle, XCircle, ArrowRight,
  BookOpen, Calendar, Building, Globe, MapPin, Star
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/control-center';

// CSS Animation for slide-in
const slideInStyle = `
@keyframes slideInRight {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
`;

// Inject animation style
if (typeof document !== 'undefined' && !document.getElementById('rec-engine-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'rec-engine-styles';
  styleEl.textContent = slideInStyle;
  document.head.appendChild(styleEl);
}

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  primary: '#1D8F4E',
  primaryLight: '#E8F5EE',
  secondary: '#1B3A6B',
  secondaryLight: '#EEF2F7',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
};

const NEED_ICONS = {
  sell_products: ShoppingBag,
  develop_business: TrendingUp,
  learn_skills: GraduationCap,
  get_consultation: MessageSquare,
  find_workspace: Building2,
  solve_operations: Wrench,
  improve_marketing: Megaphone,
  grow_business: Rocket,
  start_business: Lightbulb,
};

const NEED_COLORS = {
  sell_products: '#EC4899',
  develop_business: '#8B5CF6',
  learn_skills: '#F59E0B',
  get_consultation: '#3B82F6',
  find_workspace: '#10B981',
  solve_operations: '#EF4444',
  improve_marketing: '#06B6D4',
  grow_business: '#6366F1',
  start_business: '#F97316',
};

// ═══════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function getAuthHeaders() {
  const token = localStorage.getItem('hala_admin_token');
  return { Authorization: `Bearer ${token}` };
}

// ═══════════════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

function Alert({ type = 'info', children, style = {} }) {
  const configs = {
    info: { bg: '#EFF6FF', border: '#3B82F6', color: '#1D4ED8', Icon: Info },
    warning: { bg: '#FFFBEB', border: '#F59E0B', color: '#B45309', Icon: AlertTriangle },
    error: { bg: '#FEF2F2', border: '#EF4444', color: '#B91C1C', Icon: XCircle },
    success: { bg: '#F0FDF4', border: '#22C55E', color: '#15803D', Icon: CheckCircle },
  };
  const config = configs[type];
  return (
    <div style={{
      padding: '16px 20px', borderRadius: '12px', backgroundColor: config.bg,
      borderRight: `4px solid ${config.border}`, display: 'flex', alignItems: 'flex-start',
      gap: '12px', ...style
    }}>
      <config.Icon size={20} color={config.color} style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ fontSize: '14px', color: config.color, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Button({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) {
  const variants = {
    primary: { bg: COLORS.primary, color: '#fff', hover: '#157a3f' },
    secondary: { bg: COLORS.secondary, color: '#fff', hover: '#152d55' },
    outline: { bg: 'transparent', color: COLORS.text, border: COLORS.border, hover: COLORS.bg },
    ghost: { bg: 'transparent', color: COLORS.textMuted, hover: COLORS.bg },
    danger: { bg: COLORS.error, color: '#fff', hover: '#b91c1c' },
    success: { bg: COLORS.success, color: '#fff', hover: '#047857' },
  };
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px', gap: '6px' },
    md: { padding: '10px 18px', fontSize: '14px', gap: '8px' },
    lg: { padding: '12px 24px', fontSize: '15px', gap: '10px' },
  };
  const v = variants[variant];
  const s = sizes[size];
  
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: s.gap, padding: s.padding, fontSize: s.fontSize, fontWeight: 600,
        fontFamily: "'Tajawal', sans-serif",
        borderRadius: '10px', border: v.border ? `1.5px solid ${v.border}` : 'none',
        backgroundColor: v.bg, color: v.color, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1, transition: 'all 0.2s', ...style
      }}
    >
      {loading ? <RefreshCw size={16} className="animate-spin" /> : Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

function Select({ value, onChange, options, placeholder, style = {} }) {
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
        fontSize: '14px', fontFamily: "'Tajawal', sans-serif", color: COLORS.text,
        cursor: 'pointer', outline: 'none', ...style
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// NEED CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function NeedCard({ need, isSelected, onClick }) {
  const Icon = NEED_ICONS[need.need_id] || HelpCircle;
  const color = NEED_COLORS[need.need_id] || COLORS.primary;
  
  return (
    <div
      onClick={onClick}
      data-testid={`need-card-${need.need_id}`}
      style={{
        padding: '16px', borderRadius: '12px', cursor: 'pointer',
        backgroundColor: isSelected ? `${color}15` : COLORS.white,
        border: `2px solid ${isSelected ? color : COLORS.border}`,
        transition: 'all 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '40px', height: '40px', borderRadius: '10px',
          backgroundColor: `${color}15`, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={20} color={color} />
        </div>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>
            {need.name_ar}
          </h4>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
            {need.rules_count} {need.rules_count === 1 ? 'قاعدة' : 'قواعد'}
          </p>
        </div>
        {isSelected && <ChevronLeft size={20} color={color} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// RULE CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function RuleCard({ rule, onEdit, onDelete, onToggle }) {
  const needColor = NEED_COLORS[rule.need_id] || COLORS.primary;
  
  return (
    <div
      data-testid={`rule-card-${rule.rule_id}`}
      style={{
        padding: '16px 20px', borderRadius: '12px', backgroundColor: COLORS.white,
        border: `1px solid ${COLORS.border}`, marginBottom: '12px',
        opacity: rule.is_active ? 1 : 0.6,
      }}
    >
      {/* Rule Arrow Display */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px',
        flexWrap: 'wrap',
      }}>
        <span style={{
          padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
          backgroundColor: `${needColor}15`, color: needColor,
        }}>
          {rule.need_name_ar}
        </span>
        <ArrowLeft size={18} color={COLORS.textMuted} />
        <span style={{
          padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
          backgroundColor: COLORS.secondaryLight, color: COLORS.secondary,
        }}>
          {rule.category_name_ar}
        </span>
        <ArrowLeft size={18} color={COLORS.textMuted} />
        <span style={{
          padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 500,
          backgroundColor: COLORS.primaryLight, color: COLORS.primary,
        }}>
          {rule.service_name_ar}
        </span>
      </div>
      
      {/* Service inactive warning */}
      {rule.service_is_active === false && (
        <Alert type="warning" style={{ marginBottom: '12px', padding: '10px 14px' }}>
          هذه الخدمة غير نشطة ولن تظهر للمستخدم
        </Alert>
      )}
      
      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '12px', color: COLORS.textMuted }}>
          الأولوية: {rule.priority}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => onToggle(rule.rule_id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px',
              borderRadius: '6px', border: 'none', backgroundColor: 'transparent',
              color: rule.is_active ? COLORS.success : COLORS.textMuted,
              cursor: 'pointer', fontSize: '13px',
            }}
          >
            {rule.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
            {rule.is_active ? 'نشطة' : 'معطلة'}
          </button>
          <button
            onClick={() => onEdit(rule)}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px',
              borderRadius: '6px', border: 'none', backgroundColor: COLORS.secondaryLight,
              color: COLORS.secondary, cursor: 'pointer', fontSize: '13px',
            }}
          >
            <Edit2 size={14} /> تعديل
          </button>
          <button
            onClick={() => onDelete(rule.rule_id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px',
              borderRadius: '6px', border: 'none', backgroundColor: '#FEE2E2',
              color: COLORS.error, cursor: 'pointer', fontSize: '13px',
            }}
          >
            <Trash2 size={14} /> حذف
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIVE SIMULATOR COMPONENT (Enhanced with Resources)
// ═══════════════════════════════════════════════════════════════════════════════

function LiveSimulator({ needName, categoryName, serviceName, serviceDescription, serviceIsActive, serviceId, resources = [] }) {
  const isEmpty = !categoryName || !serviceName;
  const hasResources = resources.length > 0;
  
  // Group resources by type
  const resourcesByType = resources.reduce((acc, res) => {
    const type = res.resource_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(res);
    return acc;
  }, {});
  
  const RESOURCE_ICONS = {
    consultant: User,
    training_program: BookOpen,
    event: Calendar,
    workspace_package: Building,
    digital_service: Globe,
    branch_service: MapPin,
  };
  
  const RESOURCE_LABELS = {
    consultant: 'المستشارون',
    training_program: 'البرامج التدريبية',
    event: 'الفعاليات',
    workspace_package: 'مساحات العمل',
    digital_service: 'الخدمات الرقمية',
    branch_service: 'خدمات الفروع',
  };
  
  if (isEmpty) {
    return (
      <div style={{
        height: '100%', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', textAlign: 'center',
        padding: '40px', color: COLORS.textMuted,
      }}>
        <Eye size={48} strokeWidth={1.5} style={{ marginBottom: '16px', opacity: 0.5 }} />
        <p style={{ fontSize: '15px', margin: 0 }}>اختر التصنيف والخدمة لمعاينة النتيجة</p>
      </div>
    );
  }
  
  return (
    <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px', borderRadius: '12px 12px 0 0',
        backgroundColor: COLORS.secondary, color: COLORS.white,
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <Sparkles size={18} />
        <span style={{ fontSize: '14px', fontWeight: 600 }}>معاينة تجربة المستخدم</span>
      </div>
      
      {/* Chat Simulation */}
      <div style={{
        flex: 1, padding: '20px', backgroundColor: COLORS.bg,
        borderRadius: '0 0 12px 12px', border: `1px solid ${COLORS.border}`,
        borderTop: 'none', display: 'flex', flexDirection: 'column', gap: '16px',
        overflowY: 'auto',
      }}>
        {/* User Message */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <div style={{
            maxWidth: '80%', padding: '12px 16px', borderRadius: '16px 16px 4px 16px',
            backgroundColor: '#F1F5F9', fontSize: '14px', color: COLORS.text,
          }}>
            أريد <strong>{needName}</strong>
          </div>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            backgroundColor: COLORS.secondaryLight, display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <User size={16} color={COLORS.secondary} />
          </div>
        </div>
        
        {/* Hala Response */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            backgroundColor: COLORS.primaryLight, display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Bot size={16} color={COLORS.primary} />
          </div>
          <div style={{ maxWidth: '90%', flex: 1 }}>
            <div style={{
              padding: '12px 16px', borderRadius: '16px 16px 16px 4px',
              backgroundColor: COLORS.primary, color: COLORS.white,
              fontSize: '14px', marginBottom: '12px',
            }}>
              بناءً على احتياجك، نقترح عليك:
            </div>
            
            {/* Service Card Preview */}
            <div style={{
              padding: '16px', borderRadius: '12px', backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              marginBottom: '12px',
            }}>
              <div style={{
                fontSize: '11px', fontWeight: 600, color: COLORS.secondary,
                marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px',
              }}>
                {categoryName}
              </div>
              <h4 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 600, color: COLORS.text }}>
                {serviceName}
              </h4>
              {serviceDescription && (
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: COLORS.textMuted, lineHeight: 1.5 }}>
                  {serviceDescription}
                </p>
              )}
              
              {/* Resources Section */}
              {hasResources ? (
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 600, color: COLORS.text }}>
                    الخيارات المتاحة ({resources.length}):
                  </p>
                  
                  {/* Group by type */}
                  {Object.entries(resourcesByType).map(([type, items]) => {
                    const Icon = RESOURCE_ICONS[type] || CheckCircle;
                    const label = RESOURCE_LABELS[type] || type;
                    
                    return (
                      <div key={type} style={{ marginBottom: '12px' }}>
                        <div style={{ 
                          display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px',
                          fontSize: '11px', fontWeight: 600, color: COLORS.secondary,
                        }}>
                          <Icon size={12} />
                          {label}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {items.slice(0, 2).map((res, i) => (
                            <div key={i} style={{
                              padding: '10px 12px', borderRadius: '8px', backgroundColor: COLORS.bg,
                              border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: '10px',
                            }}>
                              <div style={{
                                width: '28px', height: '28px', borderRadius: '6px',
                                backgroundColor: COLORS.primaryLight, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                              }}>
                                <Icon size={14} color={COLORS.primary} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: COLORS.text }}>
                                  {res.name_ar}
                                </p>
                                {res.short_description && (
                                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: COLORS.textMuted }}>
                                    {res.short_description}
                                  </p>
                                )}
                                {res.rating && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                                    {[...Array(5)].map((_, idx) => (
                                      <Star key={idx} size={10} fill={idx < res.rating ? '#F59E0B' : '#E5E7EB'} color={idx < res.rating ? '#F59E0B' : '#E5E7EB'} />
                                    ))}
                                  </div>
                                )}
                              </div>
                              {res.price && (
                                <span style={{ fontSize: '11px', fontWeight: 600, color: COLORS.primary }}>
                                  {res.is_free ? 'مجاني' : `${res.price} ر.س`}
                                </span>
                              )}
                            </div>
                          ))}
                          {items.length > 2 && (
                            <p style={{ margin: 0, fontSize: '12px', color: COLORS.info, fontWeight: 500, textAlign: 'center' }}>
                              +{items.length - 2} خيارات أخرى
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Alert type="warning" style={{ marginBottom: '12px', padding: '10px 12px' }}>
                  هذه الخدمة لا تحتوي على عناصر عرض فعلية - أضف موارد من صفحات "عناصر الخدمة"
                </Alert>
              )}
              
              <button style={{
                width: '100%', padding: '10px', borderRadius: '8px',
                border: 'none', backgroundColor: hasResources ? COLORS.primary : COLORS.textMuted, 
                color: COLORS.white,
                fontSize: '14px', fontWeight: 600, cursor: hasResources ? 'pointer' : 'not-allowed',
                opacity: hasResources ? 1 : 0.6,
              }}>
                {hasResources ? 'ابدأ الآن' : 'غير متاح حالياً'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Warnings */}
        {serviceIsActive === false && (
          <Alert type="warning">
            <strong>تنبيه:</strong> هذه الخدمة غير نشطة حالياً ولن تظهر للمستخدمين
          </Alert>
        )}
        
        {!hasResources && serviceId && (
          <Alert type="info">
            <strong>ملاحظة:</strong> لإظهار خيارات للمستخدم، قم بإضافة عناصر عرض للخدمة من صفحة كتالوج الخدمات
          </Alert>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD/EDIT RULE PANEL
// ═══════════════════════════════════════════════════════════════════════════════

function RuleBuilderPanel({ isOpen, onClose, onSave, selectedNeed, categories, services, editingRule }) {
  const [categoryId, setCategoryId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [priority, setPriority] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resources, setResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  
  // Debug log
  console.log('RuleBuilderPanel render - isOpen:', isOpen, 'selectedNeed:', selectedNeed?.name_ar);
  
  // Filtered services based on selected category
  const filteredServices = services.filter(s => s.category_id === categoryId);
  
  // Selected names for display
  const selectedCategory = categories.find(c => c.category_id === categoryId);
  const selectedService = filteredServices.find(s => s.service_id === serviceId);
  const selectedCategoryName = selectedCategory?.name_ar || selectedCategory?.category_name_ar;
  const selectedServiceName = selectedService?.name_ar;
  
  // Fetch resources when service changes
  useEffect(() => {
    const fetchResources = async () => {
      if (!serviceId) {
        setResources([]);
        return;
      }
      setLoadingResources(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/catalog/services/${serviceId}/resources`,
          { headers: getAuthHeaders() }
        );
        setResources(res.data || []);
      } catch (e) {
        console.error('Failed to fetch resources:', e);
        setResources([]);
      }
      setLoadingResources(false);
    };
    fetchResources();
  }, [serviceId]);
  
  // Reset when panel opens or editing rule changes
  useEffect(() => {
    if (isOpen) {
      if (editingRule) {
        setCategoryId(editingRule.category_id || '');
        setServiceId(editingRule.service_id || '');
        setPriority(editingRule.priority || 1);
      } else {
        setCategoryId('');
        setServiceId('');
        setPriority(1);
      }
      setError('');
      setResources([]);
    }
  }, [isOpen, editingRule]);
  
  // Reset service when category changes
  useEffect(() => {
    if (!editingRule) {
      setServiceId('');
    }
  }, [categoryId, editingRule]);
  
  const handleSubmit = async () => {
    if (!categoryId) {
      setError('يجب اختيار التصنيف');
      return;
    }
    if (!serviceId) {
      setError('يجب اختيار الخدمة');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await onSave({
        need_id: selectedNeed.need_id,
        category_id: categoryId,
        service_id: serviceId,
        priority,
        is_active: true,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'حدث خطأ أثناء حفظ القاعدة');
    }
    setLoading(false);
  };
  
  if (!isOpen) return null;
  
  const needColor = NEED_COLORS[selectedNeed?.need_id] || COLORS.primary;
  
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999 }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        }}
      />
      
      {/* Panel - Opens from RIGHT for RTL */}
      <div style={{
        position: 'absolute', top: 0, right: 0, bottom: 0, width: '900px', maxWidth: '95vw',
        backgroundColor: COLORS.white, display: 'flex', flexDirection: 'column',
        boxShadow: '-10px 0 40px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
              {editingRule ? 'تعديل قاعدة التوصية' : 'إضافة قاعدة توصية'}
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '14px', color: COLORS.textMuted }}>
              الاحتياج: <span style={{ fontWeight: 600, color: needColor }}>{selectedNeed?.name_ar}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '36px', height: '36px', borderRadius: '8px', border: 'none',
              backgroundColor: COLORS.bg, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={20} color={COLORS.textMuted} />
          </button>
        </div>
        
        {/* Content - Split Layout */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Right: Rule Builder */}
          <div style={{
            width: '50%', padding: '24px', overflowY: 'auto',
            borderLeft: `1px solid ${COLORS.border}`,
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              <Zap size={18} style={{ verticalAlign: 'middle', marginLeft: '8px' }} />
              بناء القاعدة
            </h3>
            
            {error && <Alert type="error" style={{ marginBottom: '20px' }}>{error}</Alert>}
            
            {/* Step 1: Category */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block', marginBottom: '8px', fontSize: '14px',
                fontWeight: 600, color: COLORS.text,
              }}>
                1. اختيار تصنيف الخدمة <span style={{ color: COLORS.error }}>*</span>
              </label>
              <Select
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
                placeholder="-- اختر التصنيف --"
                options={categories.map(c => ({
                  value: c.category_id,
                  label: c.name_ar || c.category_name_ar
                }))}
              />
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
                التصنيف يُستخدم لتجميع الخدمات المتشابهة
              </p>
            </div>
            
            {/* Step 2: Service */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block', marginBottom: '8px', fontSize: '14px',
                fontWeight: 600, color: COLORS.text,
              }}>
                2. اختيار الخدمة <span style={{ color: COLORS.error }}>*</span>
              </label>
              <Select
                value={serviceId}
                onChange={e => setServiceId(e.target.value)}
                placeholder={categoryId ? '-- اختر الخدمة --' : '-- اختر التصنيف أولاً --'}
                options={filteredServices.map(s => ({
                  value: s.service_id,
                  label: `${s.name_ar}${!s.is_active ? ' (غير نشطة)' : ''}`
                }))}
                style={{ opacity: categoryId ? 1 : 0.6 }}
              />
              {filteredServices.length === 0 && categoryId && (
                <Alert type="warning" style={{ marginTop: '8px', padding: '10px 14px' }}>
                  لا توجد خدمات في هذا التصنيف
                </Alert>
              )}
            </div>
            
            {/* Step 3: Priority */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block', marginBottom: '8px', fontSize: '14px',
                fontWeight: 600, color: COLORS.text,
              }}>
                3. ترتيب العرض (اختياري)
              </label>
              <input
                type="number"
                value={priority}
                onChange={e => setPriority(parseInt(e.target.value) || 1)}
                min={1}
                max={99}
                style={{
                  width: '100px', padding: '12px 14px', borderRadius: '10px',
                  border: `1.5px solid ${COLORS.border}`, fontSize: '14px',
                  fontFamily: "'Tajawal', sans-serif",
                }}
              />
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
                الأولوية الأقل تظهر أولاً (1 = الأعلى أولوية)
              </p>
            </div>
            
            {/* Live Rule Preview */}
            {categoryId && serviceId && (
              <div style={{
                padding: '16px', borderRadius: '12px', backgroundColor: COLORS.primaryLight,
                border: `1px solid ${COLORS.primary}30`,
              }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: COLORS.primary }}>
                  التوصية الحالية:
                </h4>
                <p style={{ margin: 0, fontSize: '14px', color: COLORS.text, lineHeight: 1.7 }}>
                  إذا كان احتياج المستخدم: <strong style={{ color: needColor }}>{selectedNeed?.name_ar}</strong>
                  <br />
                  فسيتم توجيهه إلى: <strong>{selectedCategoryName}</strong>
                  <br />
                  وسيتم عرض الخدمة: <strong style={{ color: COLORS.primary }}>{selectedServiceName}</strong>
                </p>
              </div>
            )}
          </div>
          
          {/* Left: Live Simulator */}
          <div style={{ width: '50%', backgroundColor: COLORS.bg }}>
            <LiveSimulator
              needName={selectedNeed?.name_ar}
              categoryName={selectedCategoryName}
              serviceName={selectedServiceName}
              serviceDescription={selectedService?.short_description_ar || selectedService?.description_ar}
              serviceIsActive={selectedService?.is_active}
              serviceId={serviceId}
              resources={resources}
            />
            {loadingResources && (
              <div style={{ textAlign: 'center', padding: '10px', color: COLORS.textMuted, fontSize: '13px' }}>
                جاري تحميل عناصر العرض...
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '12px',
          backgroundColor: COLORS.bg,
        }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button
            variant="success"
            icon={Check}
            onClick={handleSubmit}
            loading={loading}
            disabled={!categoryId || !serviceId}
          >
            {editingRule ? 'حفظ التعديلات' : 'حفظ القاعدة'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function RecommendationEnginePage() {
  const [needs, setNeeds] = useState([]);
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({});
  const [exampleRule, setExampleRule] = useState(null);
  
  const [selectedNeed, setSelectedNeed] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Fetch a real example rule for display
  const fetchExampleRule = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recommendations/rules`, {
        headers: getAuthHeaders()
      });
      if (res.data && res.data.length > 0) {
        // Get first rule that has all required fields
        const validRule = res.data.find(r => r.need_name_ar && r.category_name_ar && r.service_name_ar);
        setExampleRule(validRule || res.data[0]);
      } else {
        setExampleRule(null);
      }
    } catch (e) {
      console.error('Failed to fetch example rule:', e);
      setExampleRule(null);
    }
  }, []);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [needsRes, statsRes, catsRes, svcsRes] = await Promise.all([
        axios.get(`${API}/recommendations/needs`, { headers: getAuthHeaders() }),
        axios.get(`${API}/recommendations/stats`, { headers: getAuthHeaders() }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/categories`, { headers: getAuthHeaders() }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/services`, { headers: getAuthHeaders() }),
      ]);
      
      setNeeds(needsRes.data);
      setStats(statsRes.data);
      setCategories(catsRes.data);
      // Handle both array and {items: []} response formats
      const svcsData = svcsRes.data.items || svcsRes.data;
      setServices(Array.isArray(svcsData) ? svcsData : []);
      
      // Fetch example rule
      await fetchExampleRule();
      
      // Auto-select first need with rules, or first need
      if (needsRes.data.length > 0 && !selectedNeed) {
        const needWithRules = needsRes.data.find(n => n.rules_count > 0);
        setSelectedNeed(needWithRules || needsRes.data[0]);
      }
    } catch (e) {
      console.error('Failed to fetch data:', e);
    }
    setLoading(false);
  }, [selectedNeed, fetchExampleRule]);
  
  // Fetch rules for selected need
  const fetchRules = useCallback(async () => {
    if (!selectedNeed) return;
    
    try {
      const res = await axios.get(`${API}/recommendations/rules`, {
        params: { need_id: selectedNeed.need_id },
        headers: getAuthHeaders()
      });
      setRules(res.data);
    } catch (e) {
      console.error('Failed to fetch rules:', e);
    }
  }, [selectedNeed]);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    fetchRules();
  }, [selectedNeed, fetchRules]);
  
  // Handlers
  const handleSaveRule = async (data) => {
    if (editingRule) {
      await axios.put(`${API}/recommendations/rules/${editingRule.rule_id}`, data, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API}/recommendations/rules`, data, { headers: getAuthHeaders() });
    }
    fetchData();
    fetchRules();
    setEditingRule(null);
  };
  
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه القاعدة؟')) return;
    
    await axios.delete(`${API}/recommendations/rules/${ruleId}`, { headers: getAuthHeaders() });
    fetchData();
    fetchRules();
  };
  
  const handleToggleRule = async (ruleId) => {
    await axios.post(`${API}/recommendations/rules/${ruleId}/toggle`, {}, { headers: getAuthHeaders() });
    fetchRules();
  };
  
  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setShowBuilder(true);
  };
  
  const needColor = selectedNeed ? (NEED_COLORS[selectedNeed.need_id] || COLORS.primary) : COLORS.primary;
  
  return (
    <div style={{ padding: '24px', backgroundColor: COLORS.bg, minHeight: '100vh' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '28px', fontWeight: 700, color: COLORS.text }}>
          محرك التوصيات
        </h1>
        <p style={{ margin: 0, fontSize: '15px', color: COLORS.textMuted }}>
          قم بربط احتياجات المستخدمين بالخدمات المناسبة لعرض أفضل النتائج داخل هلا
        </p>
      </div>
      
      {/* Info Box - Dynamic Example */}
      <Alert type="info" style={{ marginBottom: '24px' }}>
        <strong>كيف يعمل النظام؟</strong>
        <br />
        يقوم هلا بتحليل طلب المستخدم (مثل: أريد تطوير مشروعي)،
        ثم يربط الاحتياج بالخدمات المناسبة بناءً على قواعد التوصية التي يتم إعدادها في النظام.
        <br /><br />
        <strong>القاعدة:</strong> احتياج المستخدم ← تصنيف الخدمة ← الخدمة
        
        {/* Dynamic Example Section */}
        {stats.total_rules > 0 && exampleRule ? (
          <>
            <br /><br />
            <strong>مثال من القواعد الحالية:</strong>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px',
              padding: '10px 14px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '8px',
              flexWrap: 'wrap',
            }}>
              <span style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                backgroundColor: `${COLORS.primary}15`, color: COLORS.primary,
              }}>
                {exampleRule.need_name_ar}
              </span>
              <ArrowLeft size={16} color={COLORS.textMuted} />
              <span style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 500,
                backgroundColor: `${COLORS.secondary}15`, color: COLORS.secondary,
              }}>
                {exampleRule.category_name_ar}
              </span>
              <ArrowLeft size={16} color={COLORS.textMuted} />
              <span style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: 500,
                backgroundColor: `${COLORS.success}15`, color: COLORS.success,
              }}>
                {exampleRule.service_name_ar}
              </span>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
              <strong>النتيجة:</strong> عند طلب المستخدم "{exampleRule.need_name_ar}"، سيتم عرض خدمة "{exampleRule.service_name_ar}" له
            </p>
          </>
        ) : (
          <>
            <br /><br />
            <div style={{
              padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '8px',
              border: `1px dashed ${COLORS.warning}`, marginTop: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={18} color={COLORS.warning} />
                <strong style={{ color: COLORS.warning }}>لا توجد قواعد توصية مضافة حالياً</strong>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                قم بإضافة قواعد لربط احتياجات المستخدم بالخدمات المناسبة.
                اختر احتياج من القائمة على اليمين ثم اضغط "إضافة قاعدة".
              </p>
            </div>
          </>
        )}
      </Alert>
      
      {/* Stats Row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px',
        marginBottom: '24px',
      }}>
        {[
          { label: 'إجمالي القواعد', value: stats.total_rules || 0, color: COLORS.secondary },
          { label: 'القواعد النشطة', value: stats.active_rules || 0, color: COLORS.success },
          { label: 'الاحتياجات المربوطة', value: stats.needs_with_rules || 0, color: COLORS.primary },
          { label: 'احتياجات بدون قواعد', value: stats.needs_without_rules || 0, color: COLORS.warning },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '20px', borderRadius: '12px', backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '14px', color: COLORS.textMuted }}>{stat.label}</div>
          </div>
        ))}
      </div>
      
      {/* Main Layout - Split Screen */}
      <div style={{
        display: 'grid', gridTemplateColumns: '350px 1fr', gap: '24px',
        minHeight: '600px',
      }}>
        {/* Right Panel: Needs List */}
        <div style={{
          backgroundColor: COLORS.white, borderRadius: '16px',
          border: `1px solid ${COLORS.border}`, overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px', borderBottom: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.bg,
          }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              احتياجات المستخدم
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
              اختر الاحتياج لعرض أو تعديل قواعد التوصية
            </p>
          </div>
          
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {needs.map(need => (
              <NeedCard
                key={need.need_id}
                need={need}
                isSelected={selectedNeed?.need_id === need.need_id}
                onClick={() => setSelectedNeed(need)}
              />
            ))}
          </div>
        </div>
        
        {/* Left Panel: Rules List */}
        <div style={{
          backgroundColor: COLORS.white, borderRadius: '16px',
          border: `1px solid ${COLORS.border}`, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px', borderBottom: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            backgroundColor: COLORS.bg,
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
                قواعد التوصية
                {selectedNeed && (
                  <span style={{ marginRight: '8px', color: needColor }}>
                    - {selectedNeed.name_ar}
                  </span>
                )}
              </h3>
            </div>
            <Button
              variant="primary"
              icon={Plus}
              onClick={() => { 
                console.log('Add Rule clicked, selectedNeed:', selectedNeed);
                setEditingRule(null); 
                setShowBuilder(true); 
              }}
              disabled={!selectedNeed}
              data-testid="add-rule-button"
            >
              إضافة قاعدة
            </Button>
          </div>
          
          {/* Rules Content */}
          <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
            {!selectedNeed ? (
              <div style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                color: COLORS.textMuted,
              }}>
                <Info size={48} strokeWidth={1.5} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <p style={{ fontSize: '15px', margin: 0 }}>اختر احتياج من القائمة لعرض القواعد</p>
              </div>
            ) : rules.length === 0 ? (
              <div style={{
                height: '100%', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                padding: '40px',
              }}>
                <div style={{
                  width: '80px', height: '80px', borderRadius: '50%',
                  backgroundColor: `${needColor}15`, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', marginBottom: '20px',
                }}>
                  {(() => {
                    const Icon = NEED_ICONS[selectedNeed.need_id] || HelpCircle;
                    return <Icon size={36} color={needColor} />;
                  })()}
                </div>
                <h4 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: COLORS.text }}>
                  لا توجد قواعد توصية لهذا الاحتياج
                </h4>
                <p style={{ margin: '0 0 24px', fontSize: '14px', color: COLORS.textMuted, maxWidth: '400px' }}>
                  قم بإضافة قاعدة لربط هذا الاحتياج بالخدمات المناسبة
                </p>
                
                <Alert type="warning" style={{ marginBottom: '20px', maxWidth: '450px' }}>
                  لن يتم اقتراح أي خدمات لهذا الاحتياج حتى يتم إضافة قواعد
                </Alert>
                
                <Button
                  variant="primary"
                  icon={Plus}
                  size="lg"
                  onClick={() => { setEditingRule(null); setShowBuilder(true); }}
                >
                  إضافة قاعدة توصية
                </Button>
              </div>
            ) : (
              <>
                {rules.map(rule => (
                  <RuleCard
                    key={rule.rule_id}
                    rule={rule}
                    onEdit={handleEditRule}
                    onDelete={handleDeleteRule}
                    onToggle={handleToggleRule}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Rule Builder Panel */}
      <RuleBuilderPanel
        isOpen={showBuilder}
        onClose={() => { setShowBuilder(false); setEditingRule(null); }}
        onSave={handleSaveRule}
        selectedNeed={selectedNeed}
        categories={categories}
        services={services}
        editingRule={editingRule}
      />
    </div>
  );
}
